import React, { useRef, useState, useEffect } from "react";
import "aos/dist/aos.css";
import AOS from "aos";
import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import Banner from "./Components/Banner/Banner";
import Header from "./Components/Header/Header";
import Buildings from "./Components/Reports/Buildings/Buildings";
import DoorDetail from "./Components/Reports/DoorDetails/DoorDetail";
import DoorResiDetail from "./Components/Reports/DoorResiDetails/DoorResiDetail";
import AllDoors from "./Components/Reports/AllDoors/AllDoors";
import Footer from "./Components/Footer/Footer";
import Four04 from "./Components/404page/Four04";
import RequireAuth from "./Components/Authentication/Login/RequireAuth/RequireAuth";
import Login from "./Components/Authentication/Login/Login";
import Sidebar from "./Components/Sidebar/Sidebar";
import Admin from "./Components/Admin/Admin";
import PrivacyPolicy from "./Components/Declaration/PrivacyPolicy";
import "./App.css";
import UploadDoc from "./Components/Reports/UploadDoc/UploadDoc";
import axios from "axios";
import { auth } from "./firebase.init";
import { useAuthState } from "react-firebase-hooks/auth";
import { v4 as uuidv4 } from "uuid";
import Review from "./Components/Review/Review";
import Tickets from "./Components/Tickets/Tickets";
import Dashboard from "./Components/Reports/Dashboard/Dashboard";
import CalendarPage from "./Components/Calendar/Calendar";
import BookInspection from "./Components/BookInspection/BookInspection";
import AnnotateImages from "./Components/AnnotateImages/AnnotateImages";
import Estimates from "./Components/Estimates/Estimates";
import RemedialProfit from "./Components/Profit/RemedialProfit";
import { useUserRole } from "./hooks/useUserRole";
import { Spinner } from "react-bootstrap";
import EscalationStrategy from "./Components/Reports/EscalationStrategy/EscalationStrategy";
import BookRemedial from "./Components/BookRemedial/BookRemedial";
import DoorScan from "./Components/DoorScan/DoorScan";

function App() {
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth <= 768
  );
  const hasRun = useRef(false);
  const [user, loadingAuth] = useAuthState(auth);
  const userData = useUserRole();

  const getUnknownUserId = () => {
    let id = localStorage.getItem("unknownUserId");
    if (!id) {
      id = uuidv4();
      localStorage.setItem("unknownUserId", id);
    }
    return id;
  };

  const formatTimestamp = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    })
      .format(date)
      .replace(",", "");
  };

  const getDeviceAndBrowser = async () => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const isMobileDevice = /Mobi|Android|iPhone|iPad/i.test(userAgent);
    const isTablet = /iPad|Tablet|PlayBook|Silk/i.test(userAgent);
    let deviceType = isTablet
      ? "Tablet"
      : isMobileDevice
        ? "Mobile"
        : "Desktop";

    let browser = "Unknown";
    if (userAgent.includes("Edg/")) browser = "Microsoft Edge";
    else if (userAgent.includes("Chrome/")) browser = "Google Chrome";
    else if (userAgent.includes("Firefox/")) browser = "Mozilla Firefox";
    else if (userAgent.includes("Safari/") && !userAgent.includes("Chrome"))
      browser = "Safari";
    else if (userAgent.includes("OPR/") || userAgent.includes("Opera"))
      browser = "Opera";

    let batteryInfo = {};
    try {
      const battery = await navigator.getBattery();
      batteryInfo = {
        level: battery.level * 100 + "%",
        charging: battery.charging ? "Yes" : "No",
      };
    } catch (error) {
      console.error("Battery API not supported:", error);
    }

    return {
      platform,
      deviceType,
      browser,
      cpuCores: navigator.hardwareConcurrency,
      networkType: navigator.connection?.effectiveType || "Unknown",
      downlinkSpeed: navigator.connection?.downlink + " Mbps",
      rttLatency: navigator.connection?.rtt + " ms" || "Unknown",
      saveDataMode: navigator.connection?.saveData ? "Enabled" : "Disabled",
      screenOrientation: window.screen.orientation?.type || "Unknown",
      devicePixelRatio: window.devicePixelRatio,
      colorDepth: window.screen.colorDepth,
      screenWidth: window.screen.availWidth,
      screenHeight: window.screen.availHeight,
      batteryInfo,
    };
  };

  const logGoogleLocation = async () => {
    try {
      const response = await axios.post(
        `https://www.googleapis.com/geolocation/v1/geolocate?key=AIzaSyAxqvYz4ClnpyEA2cbh4NVCmZq587173_o`,
        {
          considerIp: true,
        }
      );
      if (response.data.location) {
        return {
          latitude: response.data.location.lat,
          longitude: response.data.location.lng,
          accuracy: response.data.accuracy,
        };
      }
      console.error("No location data received.");
      return null;
    } catch (error) {
      console.error("Error fetching Google location:", error);
      return null;
    }
  };

  const trackAndSaveData = async () => {
    const currentUrl = window.location.href;
    const deviceData = await getDeviceAndBrowser();
    const locationData = await logGoogleLocation();
    const dataToSave = {
      deviceData,
      locationData,
      currentUrl,
      timestamp: formatTimestamp(new Date().toISOString()),
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    const timeForPath = Date.now();
    let path;
    if (user && user.uid) {
      path = `tracking/known_users/${user.uid}/${timeForPath}`;
    } else {
      path = `tracking/unknown_users/${getUnknownUserId()}/${timeForPath}`;
    }

    console.log("Data to save:", dataToSave);
    console.log("Tracking path:", path);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    AOS.init();

    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (mobile) {
        setIsCollapsed(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const noSidebarRoutes = ["/login"];
  const isNoSidebar = noSidebarRoutes.includes(location.pathname);

  const toggleSidebar = () => {
    setIsCollapsed((prev) => !prev);
  };

  const mainContainerStyle = isNoSidebar
    ? {
      marginLeft: 0,
      width: "100%",
    }
    : isMobile
      ? {
        marginLeft: 0,
        width: "100%",
        maxWidth: "100%",
      }
      : {
        marginLeft: isCollapsed ? "60px" : "250px",
        width: isCollapsed ? "calc(100% - 60px)" : "calc(100% - 250px)",
      };

  const isContractUser = user?.uid === "GxMS8DiafTgAR0QYgjwmteUz6Xq2";

  if (user && user?.email === "review@firechecked.co.uk") {
    return (
      <>
        {!noSidebarRoutes.includes(location.pathname) && (
          <Sidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
        )}

        <div
          className={`${isNoSidebar ? "no-sidebar" : "main-container"} ${isContractUser ? "contract-bg" : ""
            }`}
          style={mainContainerStyle}
        >
          <Routes>
            <Route
              path="/annotate-images"
              element={
                <>
                  <Helmet>
                    <title>Annotate Images - FireChecked.netlify.app</title>
                  </Helmet>
                  <Banner />
                  <RequireAuth>
                    <AnnotateImages />
                  </RequireAuth>
                </>
              }
            />
            <Route
              path="*"
              element={<Navigate to="/annotate-images" replace />}
            />
          </Routes>
        </div>
      </>
    );
  }

  const reviewElement = (
    <>
      <Helmet>
        <title>Review - FireChecked.netlify.app</title>
        <meta name="review" content="Review" />
      </Helmet>
      <Banner />
      <RequireAuth>
        {loadingAuth || !userData?.role ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: "80vh" }}
          >
            <div className="text-center">
              <Spinner animation="border" variant="danger" />
              <p className="mt-3 text-muted">Loading...</p>
            </div>
          </div>
        ) : userData.role === "admin" ||
          userData.role === "manager" ||
          userData.role === "inspection" ||
          userData.role === "coordinator" ||
          userData.role === "reviewer" ? (
          <Review />
        ) : (
          <Navigate to="/404" replace />
        )}
      </RequireAuth>
      <Footer />
    </>
  );

  return (
    <>
      {!noSidebarRoutes.includes(location.pathname) && (
        <Sidebar isCollapsed={isCollapsed} toggleSidebar={toggleSidebar} />
      )}

      <div
        className={`${isNoSidebar ? "no-sidebar" : "main-container"} ${isContractUser ? "contract-bg" : ""
          }`}
        style={mainContainerStyle}
      >
        <Header />

        <Helmet>
          <title>FireChecked</title>
          <meta
            name="description"
            content="FireChecked offers fully accredited, third-party inspections for fire doors and fire-stopping compartmentation."
          />
          <meta
            name="keywords"
            content="FireChecked, firechecked.co.uk, firechecked uk, best firedoor checked"
          />
          <link rel="canonical" href="https://firechecked.netlify.app/" />
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0"
          />
        </Helmet>

        <Routes>
          <Route
            path="/reports"
            element={
              <>
                <Helmet>
                  <title>Reports - FireChecked.netlify.app</title>
                  <meta name="Reports" content="Building Reports" />
                </Helmet>
                <Banner />
                <Buildings />
                <Footer />
              </>
            }
          />

          <Route
            path="/upload_doc/:uid"
            element={
              <>
                <Helmet>
                  <title>Upload Doc - FireChecked.netlify.app</title>
                  <meta name="description" content="Upload Documents" />
                </Helmet>
                <UploadDoc />
                <Footer />
              </>
            }
          />

          <Route
            path="/doors/:uid"
            element={
              <>
                <Helmet>
                  <title>All Doors - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Explore all doors available."
                  />
                </Helmet>
                <AllDoors />
                <Footer />
              </>
            }
          />

          <Route
            path="/doors/:uid/details/:doorId"
            element={
              <>
                <Helmet>
                  <title>Door Details - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Detailed information about the selected door."
                  />
                </Helmet>
                <DoorDetail />
                <Footer />
              </>
            }
          />

          <Route
            path="/doors/:uid/resi_details/:doorId"
            element={
              <>
                <Helmet>
                  <title>Door Details - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Detailed information about the selected door."
                  />
                </Helmet>
                <DoorResiDetail />
                <Footer />
              </>
            }
          />

          <Route
            path="/doors/:uid/resi_details/:doorId/setpasscode"
            element={
              <>
                <Helmet>
                  <title>Set Door Passcode - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Set a passcode for the selected residential door."
                  />
                </Helmet>

                <DoorResiDetail />
                <Footer />
              </>
            }
          />

          <Route
            path="/escalate/:uid"
            element={
              <>
                <Helmet>
                  <title>Escalate Strategy - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Track work completed and escalate non-access"
                  />
                </Helmet>
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ||
                    userData.role === "manager" ||
                    userData.role === "inspection" ||
                    userData.role === "coordinator" ||
                    userData.role === "remedial" ||
                    userData.uid === "6pml9u9AgdR9U0jaZHqq4bulQb73" ? (
                    <EscalationStrategy />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          <Route
            path="/WillowHouse"
            element={
              <>
                <Helmet>
                  <title>Building Details - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Detailed information about the selected door."
                  />
                </Helmet>
                <AllDoors />
                <Footer />
              </>
            }
          />

          <Route
            path="/WillowHouse/C/:doorId"
            element={
              <>
                <Helmet>
                  <title>Door Details - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Detailed information about the selected door."
                  />
                </Helmet>
                <DoorDetail />
                <Footer />
              </>
            }
          />

          <Route
            path="/WillowHouse/R/:doorId"
            element={
              <>
                <Helmet>
                  <title>Door Details - FireChecked.netlify.app</title>
                  <meta
                    name="description"
                    content="Detailed information about the selected door."
                  />
                </Helmet>
                <DoorResiDetail />
                <Footer />
              </>
            }
          />

          <Route
            path="/admin"
            element={
              <>
                <Helmet>
                  <title>Admin - FireChecked.netlify.app</title>
                  <meta name="admin" content="Admin" />
                </Helmet>
                <Banner />
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ? (
                    <Admin />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          <Route
            path="/dashboard"
            element={
              <>
                <Helmet>
                  <title>Dashboard - FireChecked.netlify.app</title>
                  <meta
                    name="Dashboard"
                    content="Welcome to the Safety Dashboard"
                  />
                </Helmet>
                <Dashboard />
              </>
            }
          />

          <Route path="/review" element={reviewElement} />
          <Route path="/review/:buildingId/:id" element={reviewElement} />

          <Route
            path="/profit"
            element={
              <>
                <Helmet>
                  <title>Profit Analysis - FireChecked.netlify.app</title>
                  <meta name="profit" content="Profit Analysis" />
                </Helmet>
                <Banner />
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ||
                    userData.role === "manager" ||
                    userData.role === "remedial" ? (
                    <RemedialProfit />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          <Route
            path="/tickets"
            element={
              <>
                <Helmet>
                  <title>Tickets - FireChecked.netlify.app</title>
                  <meta name="tickets" content="Tickets" />
                </Helmet>
                <Banner />
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ||
                    userData.role === "manager" ||
                    userData.role === "inspection" ||
                    userData.role === "reviewer" ||
                    userData.role === "coordinator" ||
                    userData.role === "remedial" ? (
                    <Tickets userData={userData} />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          <Route
            path="/calendar"
            element={
              <>
                <Helmet>
                  <title>Calendar - FireChecked.netlify.app</title>
                  <meta name="calendar" content="Calendar" />
                </Helmet>
                <Banner />
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ||
                    userData.role === "manager" ||
                    userData.role === "inspection" ||
                    userData.role === "coordinator" ||
                    userData.role === "reviewer" ||
                    userData.role === "remedial" ||
                    userData.role === "client" ||
                    userData.role === "inspector" ||
                    userData.role === "carpenter" ? (
                    <CalendarPage />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          <Route
            path="/estimates"
            element={
              <>
                <Helmet>
                  <title>Estimates - FireChecked.netlify.app</title>
                  <meta name="estimates" content="Estimates" />
                </Helmet>
                <Banner />
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ||
                    userData.role === "inspection" ||
                    userData.role === "remedial" ? (
                    <Estimates />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          <Route
            path="/book-inspection"
            element={
              <>
                <Helmet>
                  <title>Book Inspection - FireChecked.netlify.app</title>
                  <meta name="book-inspection" content="Book Inspection" />
                </Helmet>
                <BookInspection />
              </>
            }
          />

          <Route
            path="/book-remedial"
            element={
              <>
                <Helmet>
                  <title>Book Remedial - FireChecked.netlify.app</title>
                  <meta name="book-remedial" content="Book Remedial" />
                </Helmet>
                <BookRemedial />
              </>
            }
          />

          <Route
            path="/book-remedial/:buildingId/:id"
            element={
              <>
                <Helmet>
                  <title>Book Remedial - FireChecked.netlify.app</title>
                  <meta name="book-remedial" content="Book Remedial" />
                </Helmet>
                <BookRemedial />
              </>
            }
          />

          <Route
            path="/annotate-images"
            element={
              <>
                <Helmet>
                  <title>Annotate Images - FireChecked.netlify.app</title>
                  <meta name="annotate-images" content="Annotate Images" />
                </Helmet>
                <Banner />
                <RequireAuth>
                  {loadingAuth || !userData?.role ? (
                    <div
                      className="d-flex justify-content-center align-items-center"
                      style={{ height: "80vh" }}
                    >
                      <div className="text-center">
                        <Spinner animation="border" variant="danger" />
                        <p className="mt-3 text-muted">Loading...</p>
                      </div>
                    </div>
                  ) : userData.role === "admin" ||
                    userData.role === "annotator" ? (
                    <AnnotateImages />
                  ) : (
                    <Navigate to="/404" replace />
                  )}
                </RequireAuth>
              </>
            }
          />

          {/* <Route
            path="/login"
            element={
              <>
                <Login />
                <Footer />
              </>
            }
          /> */}
          <Route path="login" element={<Login />} />

          <Route path="*" element={<Four04 />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/scan" element={<DoorScan />} />
        </Routes>
      </div>
    </>
  );
}

export default App;
